import type {AgentProfile, ChatInputSlotProps, LeadingIconSlotProps} from '../agent-profile'
import type {TrailingVisualSlotProps} from '../agent-profile-types'
import {AGENT_INFO_REGISTRY} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'
import {COPILOT_APP_SLUG} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {CliIcon} from '../../components/CliIcon'
import {TerminalLockIcon} from '@github-ui/custom-icons/TerminalLockIcon'
import {CliPrivateIcon} from '../../components/CliPrivateIcon'
import {EmptyCliSession} from '../../components/session-logs/EmptyCliSession'
import {CLIChatInput} from '../../components/task-view/CLIChatInput'
import {BlockedTaskInput} from '../../components/task-view/BlockedTaskInput'
import {CLIModeToolbarSlot} from '../../components/task-view/CLIModeToolbarSlot'
import {computeConnectionStatus} from '../../utils/connection-status'
import {ConnectionStatus} from '../../types/session'
import type {SessionState} from '../../types/session'
import {getEffectiveSource, isSteeringSuppressed} from '../../types/origin'
import {REMOTABLE_BASE} from './remotable-base'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {effectiveRemoteControlPolicyEnabled} from '../../utils/effective-remote-control-policy'
import {TerminalIcon} from '@primer/octicons-react'
import styles from '../../components/task-view/ChatInputDisplay.module.css'
import trailingStyles from '../../components/AgentTypeTrailingVisual.module.css'

function CLILeadingIcon({staleAt, dataUpdatedAt, state, sharingStatus}: LeadingIconSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state as SessionState, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected

  const isPrivate = sharingStatus !== 'shared'

  if (isPrivate) {
    return <CliPrivateIcon showConnectedDot={isConnected} />
  }

  return <CliIcon showConnectedDot={isConnected} />
}

function RemotableChatInputSlot({
  session,
  task,
  newConversationHref,
  textAreaRef,
  onCommandSent,
  cliRemoteNoticeDismissed,
  remoteControlPolicyEnabled,
  ssoOrgIds,
}: ChatInputSlotProps) {
  const {currentUser, currentUserIsSessionOwner} = useCurrentUser()
  // Agency sessions wrap the CLI (same profile) but steering isn't wired through
  // Agency yet. See isSteeringSuppressed.
  const isSteerSuppressed = isSteeringSuppressed(getEffectiveSource(task?.origin, {artifacts: task?.artifacts}))
  const remoteSteerable = task?.remote_steerable === true
  const supportsCLICommands =
    session.agent_slug !== COPILOT_APP_SLUG && task?.agent_collaborators?.[0]?.slug !== COPILOT_APP_SLUG
  const isShared = task?.sharing_status === 'shared'
  const computeProvider = task?.compute?.provider ?? session.compute?.provider
  const effectivePolicyEnabled = effectiveRemoteControlPolicyEnabled(remoteControlPolicyEnabled, computeProvider)

  const isSessionOwner = currentUserIsSessionOwner || (!!currentUser && session.user_id === currentUser.id)

  // Sources where steering is suppressed get a neutral blocked state rather than
  // CLIChatInput's CLI-specific "/remote on" copy, which doesn't apply to them —
  // an Agency session can't be made steerable by running `copilot --remote`.
  if (isSteerSuppressed) {
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>Steering is not available for this session</BlockedTaskInput>
      </div>
    )
  }

  if (!isSessionOwner && currentUser) {
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>Only the session owner can steer this session</BlockedTaskInput>
      </div>
    )
  }

  return (
    <CLIChatInput
      session={session}
      newConversationHref={newConversationHref}
      textAreaRef={textAreaRef}
      supportsCLICommands={supportsCLICommands}
      remoteSteerable={remoteSteerable}
      remoteControlPolicyEnabled={effectivePolicyEnabled}
      ssoOrgIds={ssoOrgIds}
      staleAt={task?.stale_at}
      onCommandSent={onCommandSent}
      cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
      isShared={isShared}
      toolbarSlot={<CLIModeToolbarSlot sessionId={session.id} taskId={session.task_id} />}
    />
  )
}

function CLITrailingVisual({state, staleAt, dataUpdatedAt, sharingStatus}: TrailingVisualSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected

  const isPrivate = sharingStatus !== 'shared'

  const label = isPrivate
    ? isConnected
      ? 'Private session connected'
      : 'Private remote session'
    : isConnected
      ? 'CLI session connected'
      : 'CLI session'

  return (
    <span
      className={trailingStyles.wrapper}
      role="img"
      aria-label={label}
      data-testid={isPrivate ? 'agent-type-trailing-visual-cli-private' : 'agent-type-trailing-visual-cli'}
    >
      {isPrivate ? <TerminalLockIcon /> : <TerminalIcon />}
      {isConnected && <span data-testid="connected-dot" className={trailingStyles.connectedDot} aria-hidden="true" />}
    </span>
  )
}

/**
 * Profile overrides for the Copilot CLI agent.
 * Extends the remotable base with CLI-specific icon and identity.
 */
export const CLI_PROFILE: Partial<AgentProfile> = {
  ...REMOTABLE_BASE,
  slug: 'copilot-developer-cli',
  displayName: 'Copilot CLI',
  secondaryLabel: 'CLI',

  agentInfo: AGENT_INFO_REGISTRY['copilot-developer-cli'],

  slots: {
    LeadingIcon: CLILeadingIcon,
    ChatInput: RemotableChatInputSlot,
    EmptyState: EmptyCliSession,
    TrailingVisual: CLITrailingVisual,
  },
}
